import React, { memo } from "react";
import PropTypes from "prop-types";
import "./PhotoCapture.css";

function PhotoCaptureView({
  loading,
  status,
  errorMsg,
  cameraOpen,
  scanAddress,
  capturedImagePreview,
  confidenceResult,
  
  fileInputRef,
  videoRef,
  canvasRef,
  
  onFileSelect,
  onCaptureFromCamera,
  onStopCamera,
  onUploadPhoto,
  onOpenPicker,
  onDone,
  onTryAgain
}) {
  if (loading) return null;

  const renderConfidenceBanner = () => {
    if (!confidenceResult?.evaluation) return null;
    const { level, flag, action } = confidenceResult.evaluation;
    if (!flag) return null;

    const isReject = action === "reject";
    const isLow = level === "Low" && !isReject;

    const styles = {
      reject: { bg: "#fee2e2", border: "#f87171", text: "#991b1b", sub: "#7f1d1d" },
      low: { bg: "#fee2e2", border: "#f87171", text: "#991b1b", sub: "#7f1d1d" },
      medium: { bg: "#fff3cd", border: "#f59e0b", text: "#92400e", sub: "#78350f" },
    };

    let style = styles.medium;
    let message;

    const dist = confidenceResult.distanceMetres ?? "?";
    const scanAcc = confidenceResult.coordAcc ?? null;
    const photoAcc = confidenceResult.photoCoords?.acc != null ? Math.round(confidenceResult.photoCoords.acc) : null;

    if (isReject) {
      style = styles.reject;
      message = `🚫 High confidence rejection — you are ${dist} m from the scan point. Flagged for review.`;
    } else if (level === "Medium") {
      style = styles.medium;
      message = `⚠️ Medium GPS confidence — ${dist} m from scan point. Logged for review.`;
    } else {
      style = styles.low;
      message = `⚠️ Low GPS confidence — ${dist} m from scan point. Flagged for review.`;
    }

    return (
      <div style={{
        backgroundColor: style.bg, border: `1px solid ${style.border}`, borderRadius: "8px",
        padding: "12px 15px", marginBottom: "15px", fontSize: "0.85rem", color: style.text, lineHeight: "1.5"
      }}>
        <strong>{message}</strong>
        <div style={{ marginTop: "5px", fontSize: "0.78rem", color: style.sub }}>
          You can still upload and continue.
          {scanAcc != null && <span> &nbsp;|&nbsp; 📡 Scan: ±{scanAcc} m</span>}
          {photoAcc != null && <span> &nbsp;|&nbsp; 📸 Photo: ±{photoAcc} m</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="pc-card">
      <h3 className="pc-title">Capture Photo</h3>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => onFileSelect(e.target.files?.[0])}
        style={{ display: "none" }}
      />

      {cameraOpen && (
        <div className="pc-camera-preview">
          <video ref={videoRef} className="pc-video" autoPlay playsInline />
          <canvas ref={canvasRef} style={{ display: "none" }} />
          <button onClick={onCaptureFromCamera}>Take Photo</button>
          <button onClick={onStopCamera}>Cancel</button>
        </div>
      )}

      {capturedImagePreview && !status && !cameraOpen && (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          {renderConfidenceBanner()}
          <img
            src={capturedImagePreview}
            alt="Captured preview"
            style={{ maxWidth: "100%", maxHeight: "400px", borderRadius: "8px", marginBottom: "15px" }}
          />
          <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={onUploadPhoto} style={{ backgroundColor: "#2e7d32", borderColor: "#2e7d32", color: "white" }}>
              Upload This Pic
            </button>
            <button onClick={onOpenPicker}>Take Another Photo</button>
            <button onClick={onDone} style={{ backgroundColor: "#757575", borderColor: "#757575" }}>
              Back to Dashboard
            </button>
          </div>
        </div>
      )}

      {!cameraOpen && !status && !capturedImagePreview && (
        <>
          <div style={{
            backgroundColor: "#e8f4fd", border: "1px solid #b3d9f2", borderRadius: "8px",
            padding: "12px 15px", marginBottom: "15px", fontSize: "0.85rem", color: "#1a5276", lineHeight: "1.4"
          }}>
            📍 <strong>GPS must be ON</strong> before taking the photo. Enable Location Tags in your camera settings.
          </div>
          <button onClick={onOpenPicker}>Open Camera</button>
          <button onClick={onDone} style={{ marginTop: "10px", marginBottom: "20px", backgroundColor: "#757575", borderColor: "#757575" }}>
            Back to Dashboard
          </button>
        </>
      )}

      {status && status !== "error" && status !== "uploading" && status !== "uploaded" && (
        <>
          <div className="pc-status-message" style={{ textAlign: "center", marginTop: "10px", fontWeight: "bold" }}>
            Status: {status}
          </div>
          <button onClick={onDone} style={{ marginTop: "10px", marginBottom: "20px", backgroundColor: "#757575", borderColor: "#757575" }}>
            Back to Dashboard
          </button>
        </>
      )}

      {(status === "uploading" || status === "uploaded") && (
        <div className="pc-status-message" style={{ textAlign: "center", marginTop: "10px", fontWeight: "bold" }}>
          Status: {status}
        </div>
      )}

      {status === "error" && (
        <div className="pc-error">
          <div style={{ marginBottom: "15px" }}>
            <strong>Location:</strong>
            <div style={{ marginTop: "5px", color: "#666" }}>{scanAddress}</div>
          </div>
          <div style={{ color: "#d32f2f", marginBottom: "15px", whiteSpace: "pre-line" }}>
            {errorMsg}
          </div>
          <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
            <button onClick={onTryAgain}>Try Again</button>
            <button onClick={onDone} style={{ backgroundColor: "#757575", borderColor: "#757575" }}>
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

PhotoCaptureView.propTypes = {
  loading: PropTypes.bool.isRequired,
  status: PropTypes.string,
  errorMsg: PropTypes.string,
  cameraOpen: PropTypes.bool.isRequired,
  scanAddress: PropTypes.string.isRequired,
  capturedImagePreview: PropTypes.string,
  confidenceResult: PropTypes.object,
  
  fileInputRef: PropTypes.object,
  videoRef: PropTypes.object,
  canvasRef: PropTypes.object,
  
  onFileSelect: PropTypes.func.isRequired,
  onCaptureFromCamera: PropTypes.func.isRequired,
  onStopCamera: PropTypes.func.isRequired,
  onUploadPhoto: PropTypes.func.isRequired,
  onOpenPicker: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  onTryAgain: PropTypes.func.isRequired
};

export default memo(PhotoCaptureView);
